import React, { useContext, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { ClientInternalNoteFieldType } from 'types/FieldTypes';
import { useTranslation } from 'react-i18next';
import FormFieldContext from 'components/forms/FormFieldContext';
import { useGlobalContext } from 'GlobalContext';
import { convertTabsToSpaces } from 'services/utils/convertTextareaData';
import FieldWrapper from 'components/forms/FieldWrapper';
import FieldViewMode from './elements/FieldViewMode';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';

const ClientInternalNote: React.FC<ClientInternalNoteFieldType & { viewKey: string }> = ({ 
    viewKey, label, value, helperText, tooltipText, placeholder, 
    rows = 2, alignment, disabled, emptyLabel, isEditable
}) => {
    const { t } = useTranslation();
    const { editing, updatedData, setUpdatedData } = useContext(FormFieldContext);
    const { setUnsavedChanges } = useGlobalContext();
    const [text, setText] = useState('');
    const baseFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    
    // Sets the current value
    useEffect(() => {
        const newValue = value ?? '';
        if (text !== newValue) {
            setText(convertTabsToSpaces(newValue));
        }
    }, [value]);

    // Sets and grows the height of the field
    useLayoutEffect(() => {
        if (textareaRef.current) {
            const elem = textareaRef.current;

            // Calculate the default min height of 2 rows (or more if given) in rem
            const minHeight = (rows * 1.2) + (2 * 0.4);
            const minHeightPx = minHeight * baseFontSize;

            // Set the default height to the calculated min height
            elem.style.minHeight = `${minHeight}rem`;

            if (elem.scrollHeight > minHeightPx) {
                elem.style.height = `${elem.scrollHeight / baseFontSize}rem`;
            }
        }
    }, [text, rows]);

    // // Makes the text area focused after one click when its in view in edit mode 
    // useEffect(() => {
    //     if (editing && textareaRef.current) {
    //         textareaRef.current.focus();
    //     }
    // }, [editing]);

    // Handle text area change
    const handleTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setText(event.target.value);
        setUnsavedChanges(viewKey, event.target.value !== (value ?? ''));
        setUpdatedData({...updatedData, ['client_internal_note']: event.target.value});
    }

    return (
        <FieldWrapper
            name='client_internal_note'
            label={label}
            tooltipText={tooltipText}
            helperText={helperText}
            isEditable={isEditable}
            disabled={disabled}
            alignment={alignment}>
            {(editing) ? (
                // Edit mode
                <textarea
                    ref={textareaRef}
                    name='client_internal_note'
                    placeholder={t(placeholder)}
                    disabled={disabled}
                    value={text}
                    onChange={handleTextAreaChange}
                    className='note-form'
                />
            ) : (
                // View mode
                <FieldViewMode
                    value={text} 
                    type='textarea'
                    alignment={alignment}
                    emptyLabel={emptyLabel}
                />
            )}
        </FieldWrapper>
    );
};

export default ClientInternalNote;